import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  NOTHING_FOUND,
  SEARCH_PLACEHOLDER,
  readRecentSearches,
  rememberRecentSearch,
  searchHome,
} from '../shared/homeSearch';

function flattenGroups(groups) {
  const rows = [];
  (groups || []).forEach((group) => {
    (group.items || []).forEach((item) => rows.push(item));
  });
  return rows;
}

function Magnifier({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
      <circle cx="11" cy="11" r="6.25" fill="none" stroke={color} strokeWidth="2" />
      <path d="M16 16.5 L20 20.5" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Home search.
 * Phone and the native app use a compact Search button that opens the field.
 * Desktop keeps the field on screen. Results float over the page.
 * An empty, unfocused search leaves the rest of Home visible.
 *
 * On a phone, Cancel shares the list-row class that is width:100%. That
 * width must not apply here. A full-width Cancel that cannot shrink leaves
 * the input at width 0, so the field vanishes while the keyboard stays up.
 */
export default function HomeSearchBar({
  catalog,
  homeId,
  colors,
  onSelect,
  initialQuery = '',
  initialOpen = false,
  compact = false,
}) {
  const palette = colors || {};
  const text = palette.text || '#1a1a1a';
  const muted = palette.textSecondary || '#666';
  const placeholder = palette.textMuted || '#999';
  const surface = palette.surface || '#fff';
  const alt = palette.surfaceAlt || '#f9fafb';
  const line = palette.borderLight || palette.border || '#e5e7eb';
  const brand = palette.primary || '#59c4c4';
  const dark = text === '#f9fafb';
  const fieldFill = dark ? (palette.surfaceAlt || '#333') : '#f3f4f6';
  const fieldBorder = dark ? (palette.border || '#404040') : '#e4e4e7';
  const storage = typeof window !== 'undefined' ? window.localStorage : null;
  const inputRef = useRef(null);
  const seeded = Boolean(String(initialQuery || '').trim()) || initialOpen;
  const [query, setQuery] = useState(initialQuery);
  const [focused, setFocused] = useState(seeded);
  const [opened, setOpened] = useState(!compact || seeded);
  const [activeKey, setActiveKey] = useState('');
  const [recent, setRecent] = useState(() => readRecentSearches(storage, homeId));
  const groups = useMemo(() => searchHome(catalog, query), [catalog, query]);
  const flat = useMemo(() => flattenGroups(groups), [groups]);
  const trimmed = query.trim();
  const showField = !compact || opened;
  const showResults = showField && trimmed.length > 0;
  const showRecent = showField && focused && !showResults && recent.length > 0;
  const open = showResults || showRecent;
  const showCancel = showField && (compact || focused || trimmed.length > 0);

  useEffect(() => {
    setRecent(readRecentSearches(storage, homeId));
  }, [homeId, storage]);

  useEffect(() => {
    if (!flat.length) {
      setActiveKey('');
      return;
    }
    setActiveKey((current) => (flat.some((item) => item.key === current) ? current : flat[0].key));
  }, [flat]);

  useEffect(() => {
    if (!showField || !compact || !inputRef.current) return;
    inputRef.current.focus();
    setFocused(true);
  }, [showField, compact]);

  const choose = (item) => {
    if (!item || !onSelect) return;
    const nextRecent = rememberRecentSearch(storage, homeId, query);
    setRecent(nextRecent);
    setQuery('');
    setFocused(false);
    if (compact) setOpened(false);
    if (inputRef.current) inputRef.current.blur();
    onSelect(item);
  };

  const pickRecent = (label) => {
    setQuery(label);
    setFocused(true);
    if (inputRef.current) inputRef.current.focus();
  };

  const clear = () => {
    setQuery('');
    setFocused(true);
    if (inputRef.current) inputRef.current.focus();
  };

  const cancel = () => {
    setQuery('');
    setFocused(false);
    if (compact) setOpened(false);
    if (inputRef.current) inputRef.current.blur();
  };

  const openSearch = () => {
    setOpened(true);
    setFocused(true);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
      return;
    }
    if (!flat.length) return;
    const index = Math.max(0, flat.findIndex((item) => item.key === activeKey));
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = flat[Math.min(flat.length - 1, index + 1)];
      if (next) setActiveKey(next.key);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const next = flat[Math.max(0, index - 1)];
      if (next) setActiveKey(next.key);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const picked = flat.find((item) => item.key === activeKey) || flat[0];
      choose(picked);
    }
  };

  const rowStyle = (active) => ({
    width: '100%',
    textAlign: 'left',
    background: active ? alt : 'transparent',
    border: 'none',
    borderTop: `1px solid ${line}`,
    padding: '0.6rem 0.9rem',
    minHeight: '44px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    display: 'block',
  });

  if (!showField) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '0 0 1.25rem' }}>
        <button
          type="button"
          className="app-menu-row home-search-open"
          aria-label={SEARCH_PLACEHOLDER}
          onClick={openSearch}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            width: 'auto',
            maxWidth: 'none',
            flex: '0 0 auto',
            height: '44px',
            padding: '0 0.9rem',
            borderRadius: '12px',
            border: `1px solid ${fieldBorder}`,
            background: fieldFill,
            color: text,
            fontWeight: 650,
            fontSize: '1rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Magnifier color={muted} />
          Search
        </button>
      </div>
    );
  }

  return (
    <form
      className="home-search-form"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        const picked = flat.find((item) => item.key === activeKey) || flat[0];
        if (picked) choose(picked);
      }}
      style={{ position: 'relative', width: '100%', margin: '0 0 1.25rem', minWidth: 0 }}
    >
      <style>{`
        .home-search-input::placeholder { color: ${placeholder}; opacity: 1; }
        .home-search-input::-webkit-search-cancel-button,
        .home-search-input::-webkit-search-decoration { -webkit-appearance: none; appearance: none; display: none; }
        .home-search-input:focus {
          outline: none !important;
          border-color: transparent !important;
          box-shadow: none !important;
        }
      `}</style>
      {/* Cancel is only as wide as the word. The field keeps a 10rem floor and
          grows. The input fills a grid column so Safari cannot flex it to 0. */}
      <div className="home-search-row" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', width: '100%', minWidth: 0 }}>
        <div
          className="home-search-field"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls="home-search-results"
          style={{
            flex: '1 1 auto',
            minWidth: '10rem',
            width: 'auto',
            maxWidth: '100%',
            display: 'grid',
            gridTemplateColumns: trimmed ? 'auto minmax(0, 1fr) auto' : 'auto minmax(0, 1fr)',
            alignItems: 'center',
            columnGap: '0.55rem',
            background: fieldFill,
            color: text,
            border: `1px solid ${focused ? brand : fieldBorder}`,
            borderRadius: '12px',
            minHeight: '48px',
            padding: '0 0.45rem 0 0.85rem',
            boxSizing: 'border-box',
            boxShadow: focused ? '0 0 0 4px rgba(89, 196, 196, 0.18)' : '0 1px 2px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Magnifier color={muted} />
          <input
            ref={inputRef}
            className="home-search-input"
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label={SEARCH_PLACEHOLDER}
            aria-autocomplete="list"
            aria-controls="home-search-results"
            aria-activedescendant={showResults && activeKey ? `home-search-${activeKey}` : undefined}
            placeholder={SEARCH_PLACEHOLDER}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setFocused(true);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={onKeyDown}
            style={{
              display: 'block',
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              minHeight: '44px',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: text,
              fontSize: '16px',
              fontFamily: 'inherit',
              padding: '0.7rem 0',
              boxShadow: 'none',
              boxSizing: 'border-box',
              WebkitAppearance: 'none',
              appearance: 'none',
            }}
          />
          {trimmed ? (
            <button
              type="button"
              className="app-menu-row home-search-clear"
              aria-label="Clear"
              onMouseDown={(event) => event.preventDefault()}
              onClick={clear}
              style={{
                width: '36px',
                height: '36px',
                minWidth: '36px',
                minHeight: '36px',
                border: 'none',
                borderRadius: '999px',
                background: 'transparent',
                color: muted,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                padding: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                <path d="M3 3 L11 11 M11 3 L3 11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          ) : null}
        </div>
        {showCancel ? (
          <button
            type="button"
            className="app-menu-row home-search-cancel"
            onMouseDown={(event) => event.preventDefault()}
            onClick={cancel}
            style={{
              border: 'none',
              background: 'transparent',
              color: text,
              fontWeight: 650,
              fontSize: '1rem',
              fontFamily: 'inherit',
              padding: '0 0.15rem',
              minHeight: '44px',
              cursor: 'pointer',
              flex: '0 0 auto',
              width: 'auto',
              maxWidth: 'none',
            }}
          >
            Cancel
          </button>
        ) : null}
      </div>
      {open ? (
        <div
          id="home-search-results"
          role="listbox"
          aria-label="Search results"
          style={{
            position: 'absolute',
            zIndex: 30,
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            background: surface,
            color: text,
            border: `1px solid ${fieldBorder}`,
            borderRadius: '12px',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.16)',
            maxHeight: 'min(60vh, 28rem)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {showRecent ? (
            <div>
              <div style={{ padding: '0.7rem 0.9rem 0.3rem', color: muted, fontSize: '0.8rem', fontWeight: 700 }}>
                Recent
              </div>
              {recent.map((label) => (
                <button
                  key={label}
                  type="button"
                  className="app-menu-row"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pickRecent(label)}
                  style={rowStyle(false)}
                >
                  <span style={{ display: 'block', color: text, fontWeight: 650 }}>{label}</span>
                </button>
              ))}
            </div>
          ) : null}
          {showResults && groups.length === 0 ? (
            <p role="status" style={{ margin: 0, padding: '0.9rem', color: muted }}>
              {NOTHING_FOUND}
            </p>
          ) : null}
          {showResults && groups.map((group) => (
            <div key={group.id} role="group" aria-label={group.label}>
              <div style={{ padding: '0.7rem 0.9rem 0.3rem', color: muted, fontSize: '0.8rem', fontWeight: 700 }}>
                {group.label}
              </div>
              {group.items.map((item) => {
                const active = item.key === activeKey;
                return (
                  <button
                    key={item.key}
                    id={`home-search-${item.key}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className="app-menu-row"
                    onMouseEnter={() => setActiveKey(item.key)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(item)}
                    style={rowStyle(active)}
                  >
                    <span style={{ display: 'block', color: text, fontWeight: 650, overflowWrap: 'anywhere' }}>{item.title}</span>
                    <span style={{ display: 'block', marginTop: '0.15rem', color: muted, fontSize: '0.85rem', fontWeight: 500 }}>
                      {item.subtitle}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </form>
  );
}
